import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApiCall } from '../hooks/useApiCall';
import { checkVKIDCompatibility, isMobileDevice, isMobileUserAgent, isStandaloneApp } from '../utils/mobileUtils';
import { authLogger } from '../utils/logger';
import { getServiceHeaders } from '../utils/serviceUtils';
import { normalizeReturnUrl } from '../utils/returnUrl';
import { sessionGet, sessionRemove, sessionSet, storageGet, storageRemove, storageSet } from '../utils/storage';
import { apiUrl } from '../utils/api';
import { getSkin } from '../utils/skin';
import { SkinLegal } from './SkinBrand';
// Импортируем VK ID SDK как namespace согласно документации
import * as VKID from '@vkid/sdk';

interface VKAuthProps {
  appId: string;
  onCancel?: () => void;
  onSuccess?: () => void; // Callback для успешной авторизации
  /**
   * Вход другой страницей («Добавить аккаунт ВКонтакте», `mode=vk-add` от сервиса): без «Продолжить как …»
   * для страницы, открытой в браузере, и с prompt=login — VK ID сразу просит войти, а не предлагает ту же.
   */
  newAccount?: boolean;
}

type VKSilentPayload = {
  token?: string;
  uuid?: string;
  code?: string;
  device_id?: string;
  type?: string;
  state?: string;
  payload?: {
    token?: string;
    uuid?: string;
    code?: string;
    device_id?: string;
    type?: string;
    state?: string;
  };
};

const parseSilentPayload = (payload: VKSilentPayload) => ({
  token: payload?.token ?? payload?.payload?.token,
  uuid: payload?.uuid ?? payload?.payload?.uuid,
  code: payload?.code ?? payload?.payload?.code,
  deviceId: payload?.device_id ?? payload?.payload?.device_id,
  type: payload?.type ?? payload?.payload?.type,
  state: payload?.state ?? payload?.payload?.state,
});

const encodeState = (value: unknown): string => {
  return btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const normalizeState = (value?: string | null): string | null => {
  if (!value) return null;
  return value
    .trim()
    .replace(/ /g, '+')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .replace(/=+$/g, '');
};

const decodeState = (value: string): string => {
  const normalized = normalizeState(value);
  if (!normalized) throw new Error('Empty state');
  const padLen = (4 - (normalized.length % 4)) % 4;
  return atob(`${normalized}${'='.repeat(padLen)}`);
};

const VKAuth: React.FC<VKAuthProps> = ({ appId, onCancel, onSuccess, newAccount = false }) => {
  const { state, setLoading, setError, sendToParent, redirectToReturnUrl } = useAuth();
  const skin = getSkin();
  const { execute } = useApiCall();
  const [isVKIDLoaded, setIsVKIDLoaded] = useState(false);
  const [isVKIDReady, setIsVKIDReady] = useState(false);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);
  const [hasParentWindow, setHasParentWindow] = useState(true);
  const [hasReturnUrl, setHasReturnUrl] = useState(false);
  const oneTapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const successProcessedRef = useRef(false);
  const callbackProcessedRef = useRef(false);
  // Вход уже доведён до конца в этой загрузке страницы — повторные LOGIN_SUCCESS игнорируем молча.
  const authCompletedRef = useRef(false);

  const cleanupTempStorage = useCallback(() => {
    // PKCE-артефакты держим в localStorage (см. ниже про iOS), чистим оба хранилища.
    storageRemove('vkid_state');
    storageRemove('vkid_code_verifier');
    sessionRemove('vkid_state');
    sessionRemove('vkid_code_verifier');
    sessionRemove('vkid_return_url');
  }, []);

  // Проверяем наличие родительского окна
  useEffect(() => {
    const hasOpener = !!window.opener;
    const hasParent = window.parent !== window;
    const parentAvailable = hasOpener || hasParent;
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');

    setHasParentWindow(parentAvailable);
    setHasReturnUrl(!!returnUrl);

    authLogger.info('Проверка родительского окна (VK)', {
      hasOpener,
      hasParent,
      parentAvailable,
      hasReturnUrl: !!returnUrl,
    });
  }, []);

  // Генерируем случайную строку для state (защита от CSRF)
  const generateRandomString = (length: number = 32) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Генерируем codeVerifier для PKCE (защита от code interception)
  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  useEffect(() => {
    // Логируем информацию о мобильном окружении для отладки    
    // Проверяем совместимость VK ID с текущим окружением
    const compatibility = checkVKIDCompatibility();
    if (!compatibility.compatible) {
      console.warn('VK ID Compatibility Issues:', compatibility.issues);
      setError(`Проблемы совместимости: ${compatibility.issues.join('; ')}`);
    }
    
    // SDK уже импортирован, просто отмечаем как загруженный
    setIsVKIDLoaded(true);
  }, []);

  useEffect(() => {
    if (isVKIDLoaded && appId && containerRef.current && !isProcessingCallback) {
      const currentParams = new URLSearchParams(window.location.search);
      const hasCallbackParams = Boolean(currentParams.get('state')) && (
        Boolean(currentParams.get('payload'))
        || (Boolean(currentParams.get('code')) && Boolean(currentParams.get('device_id')))
      );

      // Если уже есть callback от VK ID, нельзя переинициализировать SDK:
      // это перезапишет state в sessionStorage и сломает CSRF-проверку.
      if (hasCallbackParams) {
        return;
      }

      try {
        console.log('Initializing VK ID SDK with appId:', appId);
        setIsVKIDReady(false);

        const parsedAppId = Number(appId);
        if (!Number.isFinite(parsedAppId)) {
          throw new Error('Invalid VK App ID');
        }
        
        // Генерируем защитные параметры
        const randomState = generateRandomString();
        
        // Получаем return_url и зашиваем его в state для Telegram WebView
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrlRaw = urlParams.get('return_url');
        const returnUrl = normalizeReturnUrl(returnUrlRaw) ?? returnUrlRaw ?? null;
        
        // Создаем объект state с random строкой и return_url
        const stateData = {
          random: randomState,
          returnUrl: returnUrl || null
        };
        
        // Кодируем state в base64 для передачи через VK ID
        const state = encodeState(stateData);
        const codeVerifier = generateCodeVerifier();
        
        // PKCE-параметры пишем в localStorage, а не sessionStorage: на мобиле флоу идёт
        // через top-level редирект на id.vk.ru и обратно, а iOS Safari ненадёжно переносит
        // sessionStorage через такой кросс-origin раунд-трип. Потеря vkid_state приводила к
        // тихому пропуску авторизации и «несохранённой сессии» только на айфоне.
        const statePersisted = storageSet('vkid_state', state) && storageSet('vkid_code_verifier', codeVerifier);
        // Подстраховка: если localStorage недоступен (приватный режим Safari) — кладём в sessionStorage,
        // он переживает редирект в той же вкладке и хотя бы даёт шанс завершить вход.
        if (!statePersisted) {
          sessionSet('vkid_state', state);
          sessionSet('vkid_code_verifier', codeVerifier);
          authLogger.warning('localStorage недоступен — PKCE-параметры кладём в sessionStorage');
        }

        // Также сохраняем return_url отдельно как fallback (для совместимости)
        // Сохраняем в оба ключа для максимальной совместимости
        if (returnUrl) {
          sessionSet('vkid_return_url', returnUrl);
          sessionSet('ssmm-return-url', returnUrl);
        }

        // Выбор режима авторизации VK ID:
        // - В popup/iframe нельзя открывать новую вкладку, иначе ломается postMessage-flow.
        // - В standalone-PWA и на мобильных InNewTab открывает внешний браузер (на iOS — Safari),
        //   куда уходит callback от VK и теряется контекст PWA (sessionStorage со state/PKCE),
        //   поэтому авторизация не возвращается в приложение. Используем Redirect в том же окне.
        const hasParent = Boolean(window.opener) || window.parent !== window;
        const shouldUseRedirect = hasParent || isStandaloneApp() || isMobileDevice();

        // Инициализируем SDK согласно актуальной документации
        const configData = {
          app: parsedAppId,
          redirectUrl: window.location.origin,
          state: state,
          codeVerifier: codeVerifier,
          scope: 'email phone', // Указываем необходимые разрешения
          mode: shouldUseRedirect
            ? VKID.ConfigAuthMode.Redirect
            : VKID.ConfigAuthMode.InNewTab,
          // Другая страница: VK ID показывает форму входа даже при открытой сессии ВКонтакте.
          // Явно и в обычном входе: Config общий на страницу, прошлый init мог оставить Login.
          prompt: [newAccount ? VKID.Prompt.Login : VKID.Prompt.Default],
        };
        
        console.log('Initializing VK ID SDK with config:', configData);
        
        VKID.Config.init(configData);
        
        console.log('VK ID SDK initialized successfully');

        // Создание экземпляра кнопки One Tap согласно документации
        const oneTap = new VKID.OneTap();
        oneTapRef.current = oneTap;

        oneTap
          .on(VKID.WidgetEvents.ERROR, (error: any) => {
            console.error('VK ID Widget Error:', error);
            handleVKIDError(error);
          })
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload: any) => {
            console.log('VK ID LOGIN_SUCCESS event received:', payload);
            handleVKIDSuccess(payload);
          })
          .on(VKID.WidgetEvents.START_LOAD, () => {
            // Шаблон кнопки уже отрендерен и кликабелен — снимаем "Инициализация...".
            // LOAD ждать нельзя: он стреляет только когда догрузится fast-auth iframe
            // (с `id.vk.ru`), а у пользователей без активной VK сессии может вообще не выстрелить.
            console.log('VK ID Widget started loading, button is ready');
            setIsVKIDReady(true);
          })
          .on(VKID.WidgetEvents.LOAD, () => {
            console.log('VK ID Widget fully loaded (fast-auth iframe ready)');
            setIsVKIDReady(true);
          });

        // Отрисовка кнопки в контейнере с обработчиками событий
        containerRef.current.innerHTML = '';
        oneTap.render({
          container: containerRef.current,
          skin: VKID.OneTapSkin.Primary,
          scheme: VKID.Scheme.LIGHT,
          lang: VKID.Languages.RUS,
          // «Продолжить как <открытая в браузере страница>» сбивал с толку, когда добавляют другую.
          fastAuthEnabled: !newAccount,
          styles: {
            height: 44,
            borderRadius: 12,
          }
        });

        // Сам HTML-шаблон вставлен синхронно внутри render() — кнопка уже видна и кликается.
        // Ставим флаг сразу, чтобы не зависеть от внутреннего таймера SDK по iframe.
        setIsVKIDReady(true);

        console.log('VK ID One Tap button initialized successfully');
      } catch (error) {
        console.error('VK ID init error:', error);
        setError('Ошибка инициализации VK ID SDK');
      }
    }
    return () => {
      setIsVKIDReady(false);
      try {
        oneTapRef.current?.close?.();
      } catch (error) {
        console.warn('VK ID cleanup error:', error);
      }
      oneTapRef.current = null;
    };
  }, [isVKIDLoaded, appId, isProcessingCallback, newAccount]);

  const handleVKIDSuccess = useCallback(async (payload: any) => {
    // В режиме mode: Redirect SDK выполняет location.assign(url) и сразу резолвит
    // Promise.resolve() => sendSuccessLoginEvent(undefined) выстреливает ДО навигации.
    // Такой "пустой" LOGIN_SUCCESS не является реальной авторизацией — игнорируем,
    // настоящий результат прилетит в URL-callback после редиректа от VK.
    const parsedPayload = parseSilentPayload(payload || {});
    const token = parsedPayload.token;
    const uuid = parsedPayload.uuid;
    const code = parsedPayload.code;
    const deviceId = parsedPayload.deviceId;
    const payloadState = parsedPayload.state;

    const hasAuthData = Boolean(token) || Boolean(code && deviceId);
    if (!hasAuthData && !payloadState) {
      console.log('⏭️ [VK AUTH] Empty LOGIN_SUCCESS payload (redirect-mode pre-navigation), ignoring');
      return;
    }

    if (successProcessedRef.current) {
      console.log('⏭️ [VK AUTH] Success already processed, skipping duplicate call');
      return;
    }
    successProcessedRef.current = true;
    console.log('⏰ [VK AUTH] Starting handleVKIDSuccess at:', new Date().toISOString());
    try {
      setLoading(true);
      setError(null);
      console.log('VK ID auth success payload:', payload);

      // Проверяем state для защиты от CSRF.
      const savedState = storageGet('vkid_state') || sessionGet('vkid_state');
      if (!savedState) {
        // Вход уже прошёл в этой загрузке страницы — это дубль события, чистить storage
        // после успеха нормально, молчим.
        if (authCompletedRef.current) {
          console.log('⏭️ [VK AUTH] Дубль LOGIN_SUCCESS после успешного входа, игнорируем');
          return;
        }

        // Иначе PKCE-хранилище не пережило редирект (приватный режим/очистка данных).
        // Раньше здесь был тихий return: пользователь видел «вошёл, а сессии нет»
        // и не мог повторить попытку без перезагрузки страницы.
        authLogger.error('[VK AUTH] Нет сохранённого state — storage не пережил редирект');
        setError('Браузер не сохранил данные входа. Отключите приватный режим или разрешите хранение данных для этого сайта и попробуйте ещё раз.');
        successProcessedRef.current = false;
        return;
      }
      const normalizedSavedState = normalizeState(savedState);
      const normalizedPayloadState = normalizeState(payloadState);
      if (!normalizedPayloadState || normalizedPayloadState !== normalizedSavedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }
      const verifiedState = normalizedPayloadState;

      // Извлекаем return_url из state параметра (для Telegram WebView)
      let returnUrlFromState = null;
      try {
        const stateData = JSON.parse(decodeState(verifiedState));
        returnUrlFromState = stateData.returnUrl;
      } catch (error) {
        console.log('Could not parse state as JSON, using fallback method');
      }

      // Бэкенд /api/vkboss/auth/auth не понимает OAuth 2.1 code/deviceId напрямую,
      // поэтому для v2-payload меняем код на access_token + user_id через SDK
      // и шлём на бэкенд в формате { method: { token: { token, vkId }, app } }.
      let backendMethodBody: Record<string, any> | null = null;

      if (token && uuid) {
        backendMethodBody = {
          vkId: { silentToken: token, uuid },
        };
      } else if (code && deviceId) {
        console.log('🔁 [VK AUTH] Exchanging OAuth 2.1 code for access_token via SDK');
        const savedCodeVerifier = storageGet('vkid_code_verifier')
          || sessionGet('vkid_code_verifier')
          || undefined;

        // Для exchangeCode нужен инициализированный Config (app + redirectUrl).
        // Если это callback-маунт, init SDK мы пропустили — подсыпаем минимальный конфиг тут.
        const currentCfg: any = (VKID.Config as any).get?.() ?? {};
        if (!currentCfg.app) {
          VKID.Config.init({
            app: Number(appId),
            redirectUrl: window.location.origin,
            codeVerifier: savedCodeVerifier ?? '',
            state: savedState,
          });
        }

        const tokenResult = await (VKID.Auth as any).exchangeCode(code, deviceId, savedCodeVerifier);
        if (!tokenResult?.access_token || !tokenResult?.user_id) {
          throw new Error('Не удалось обменять код VK ID на access_token');
        }

        backendMethodBody = {
          token: {
            token: tokenResult.access_token,
            vkId: Number(tokenResult.user_id),
          },
          app: 'vkboss',
        };
      } else {
        throw new Error('No auth payload received from VK ID');
      }

      if (backendMethodBody) {
        console.log('🔐 [VK AUTH] Sending VK auth payload to backend at:', new Date().toISOString());
        const result = await execute({
          url: apiUrl('/auth/auth?utm_source=sso-popup'),
          method: 'POST',
          headers: {
            ...getServiceHeaders(),
          },
          data: {
            method: backendMethodBody,
          },
        });
        console.log('✅ [VK AUTH] Backend auth response received at:', new Date().toISOString());

        if (result?.token) {
          console.log('👤 [VK AUTH] Fetching user data at:', new Date().toISOString());
          // Получаем данные пользователя с бэкенда используя полученный токен
          const userResponse = await execute({
            url: apiUrl('/user'),
            method: 'GET',
            headers: {
              'ssmm-token': result.token,
            },
          });
          console.log('✅ [VK AUTH] User data received at:', new Date().toISOString());

          console.log('💾 [VK AUTH] Saving to localStorage at:', new Date().toISOString());
          // Сохраняем токен в localStorage в новом формате.
          // Записываем ТОЛЬКО после успешного ответа бэка: сорвавшийся вход
          // не должен убивать уже сохранённую сессию.
          const tokenPersisted = storageSet('ssmm-token-vk', result.token);
          storageRemove('ssmm-token'); // легаси-ключ больше не актуален

          // Сохраняем кэш данных пользователя для быстрого входа
          if (userResponse && userResponse.vkId) {
            const user = {
              id: userResponse.vkId,
              name: userResponse.name && userResponse.surname
                ? `${userResponse.name} ${userResponse.surname}`
                : userResponse.name || 'VK User',
              avatar: userResponse.photo || '',
              platform: 'vk',
            };

            storageSet('ssmm-vk-user-cache', JSON.stringify({
              user,
              backendUser: userResponse
            }));
          } else {
            // Профиль не пришёл — кэш от прежнего аккаунта нельзя оставлять рядом с новым токеном.
            storageRemove('ssmm-vk-user-cache');
          }

          if (!tokenPersisted) {
            authLogger.error('[VK AUTH] Браузер не дал сохранить токен — сессия не переживёт перезагрузку');
            setError('Вход выполнен, но браузер не сохранил сессию. Отключите приватный режим, иначе при следующем открытии придётся входить заново.');
          }

          // Проверяем, нужно ли делать redirect на мобильных устройствах.
          // Читаем ДО очистки временных данных, иначе fallback-ключ vkid_return_url уже стёрт.
          const returnUrl = returnUrlFromState ||
                           sessionGet('vkid_return_url') ||
                           sessionGet('ssmm-return-url');

          // Вход доведён до конца: дальше можно чистить временные данные и глушить дубли событий.
          authCompletedRef.current = true;
          cleanupTempStorage();

          if (returnUrl && isMobileUserAgent()) {
            // Делаем redirect на return_url с токеном
            redirectToReturnUrl(result.token, returnUrl);
            return; // Выходим, чтобы не выполнить callback
          }

          // Обычный мобильный браузер (iPhone Safari): вход через приложение ВКонтакте возвращает
          // в НОВУЮ вкладку — sessionStorage с return_url там пуст, адрес есть только в state.
          // Кладём его обратно: завершение входа (sendToParent → фолбэк «нет opener») найдёт,
          // куда вернуть человека с токеном. Без этого вход «не авторизовывал» на айфоне.
          if (returnUrl) sessionSet('ssmm-return-url', returnUrl);
          
          console.log('🔄 [VK AUTH] Triggering success callback at:', new Date().toISOString());
          // Вызываем callback для перенаправления на страницу подтверждения
          if (onSuccess) {
            onSuccess();
          }
          
          console.log('🎉 [VK AUTH] Auth completed at:', new Date().toISOString());
        } else {
          throw new Error('No token received from backend');
        }
      } else {
        throw new Error('No auth payload received from VK ID');
      }
    } catch (error: any) {
      console.error('❌ [VK AUTH] Error at:', new Date().toISOString(), error);
      const errorMessage = error?.response?.data?.message || error.message || 'VK authorization failed';
      setError(errorMessage);

      // Очищаем временные данные при ошибке
      cleanupTempStorage();

      // Разрешаем повторную попытку после реальной ошибки
      successProcessedRef.current = false;

      sendToParent({
        type: 'AUTH_ERROR',
        error: errorMessage,
      });
    } finally {
      setLoading(false);
      setIsProcessingCallback(false);
      console.log('🏁 [VK AUTH] Finally block at:', new Date().toISOString());
    }
  }, [setLoading, setError, execute, onSuccess, redirectToReturnUrl, cleanupTempStorage, sendToParent, appId]);

  const handleVKIDError = (error: any) => {
    console.error('VK ID Widget Error:', error);
    setError('Ошибка виджета VK ID');
    
    // Очищаем временные данные при ошибке
    cleanupTempStorage();
  };

  const handleCancel = () => {
    // Очищаем временные данные при отмене
    cleanupTempStorage();
    
    // Просто возвращаемся к выбору авторизации, не закрываем попап
    
    // Возвращаемся к выбору типа авторизации
    if (onCancel) {
      onCancel();
    }
  };

  // Функция для обработки callback от VK ID
  const handleVKIDCallback = useCallback(() => {
    if (callbackProcessedRef.current) {
      return false;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const payload = urlParams.get('payload');
    const code = urlParams.get('code');
    const deviceId = urlParams.get('device_id');
    const type = urlParams.get('type');

    if (state && code && deviceId) {
      callbackProcessedRef.current = true;
      console.log('VK ID code_v2 callback detected, processing...');
      setIsProcessingCallback(true);

      // Старую сессию НЕ трогаем: обмен кода ещё может сорваться (мобильная сеть,
      // Safari рвёт запросы в фоне), а успешный путь всё равно перезапишет ключи.
      handleVKIDSuccess({
        code,
        device_id: deviceId,
        type: type || 'code_v2',
        state,
      });

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('device_id');
      currentUrl.searchParams.delete('type');
      currentUrl.searchParams.delete('state');
      window.history.replaceState({}, document.title, currentUrl.toString());
      return true;
    }

    if (payload && state) {
      callbackProcessedRef.current = true;
      console.log('VK ID callback detected, processing...');
      setIsProcessingCallback(true);

      // Старую сессию НЕ трогаем до успешного ответа бэка (см. ветку code_v2 выше).
      try {
        let parsedPayload: any;
        try {
          parsedPayload = JSON.parse(payload);
        } catch {
          parsedPayload = JSON.parse(decodeURIComponent(payload));
        }
        console.log('VK ID callback payload:', parsedPayload);
        
        // Проверяем, что это silent_token
        if (parsedPayload.type === 'silent_token' && parsedPayload.token) {
          // Обрабатываем как успешную авторизацию
          handleVKIDSuccess({
            token: parsedPayload.token,
            uuid: parsedPayload.uuid,
            state: state
          });
          
          // Очищаем URL от параметров VK ID callback (return_url теперь зашит в state)
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('payload');
          currentUrl.searchParams.delete('state');
          // return_url теперь передается через state параметр VK ID
          const newUrl = currentUrl.toString();
          window.history.replaceState({}, document.title, newUrl);
          return true; // Возвращаем true, если callback был обработан
        }
      } catch (error) {
        console.error('Error parsing VK ID payload:', error);
        setError('Ошибка обработки ответа VK ID');
        setIsProcessingCallback(false);
      }
    }
    return false; // Возвращаем false, если callback не был обработан
  }, [handleVKIDSuccess, setError]);

  // Проверяем URL параметры для callback от VK ID (только один раз)
  useEffect(() => {
    const wasCallbackProcessed = handleVKIDCallback();
    if (!wasCallbackProcessed) {
      console.log('No VK ID callback found, continuing with normal flow');
    }
  }, [handleVKIDCallback]);

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={handleCancel}
          disabled={isProcessingCallback}
          className="flex items-center mb-6 transition-colors text-zinc-500 hover:text-zinc-300 disabled:opacity-50"
        >
          <svg className="mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>
        
        <div className="flex items-center mb-2 space-x-3">
          <div className="overflow-hidden w-10 h-10 rounded-xl">
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
              <path d="M11.5 24h1c5.44 0 8.15 0 9.83-1.68C24 20.64 24 17.92 24 12.5v-1.02c0-5.4 0-8.12-1.67-9.8C20.65 0 17.93 0 12.5 0h-1C6.06 0 3.35 0 1.67 1.68 0 3.36 0 6.08 0 11.5v1.02c0 5.4 0 8.12 1.68 9.8C3.36 24 6.08 24 11.5 24Z" fill="#07F"/>
              <path d="M12.77 17.29c-5.47 0-8.59-3.75-8.72-9.99h2.74c.09 4.58 2.11 6.52 3.71 6.92V7.3h2.58v3.95c1.58-.17 3.24-1.97 3.8-3.95h2.58a7.62 7.62 0 0 1-3.51 4.98 7.9 7.9 0 0 1 4.11 5.01h-2.84a4.94 4.94 0 0 0-4.14-3.57v3.57h-.31Z" fill="#fff"/>
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">ВКонтакте</h1>
            <p className="text-xs text-zinc-500">{newAccount ? 'Вход другой страницей' : 'Вход через VK ID'}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {/* Mobile warning */}
        {isMobileDevice() && (
          <div className="p-3 mb-4 rounded-xl border bg-blue-500/10 border-blue-500/30">
            <p className="text-xs text-blue-400">
              💡 Используйте основной браузер для лучшей работы
            </p>
          </div>
        )}
        
        {/* No parent window warning. Со скином адрес сервиса известен и без return_url — не пугаем. */}
        {!hasParentWindow && !hasReturnUrl && !skin && (
          <div className="p-3 mb-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-amber-400">
                Страница открыта напрямую. Для корректной работы откройте через приложение.
              </p>
            </div>
          </div>
        )}

        {/* Ошибка входа: раньше сообщения из VKAuth нигде не показывались,
            и сорвавшийся вход выглядел как «просто ничего не произошло». */}
        {state.error && (
          <div className="p-3 mb-4 rounded-xl border bg-red-500/10 border-red-500/30">
            <p className="text-xs text-red-400">{state.error}</p>
          </div>
        )}

        {/* VK ID Button Container */}
        <div className="p-4 rounded-xl border bg-zinc-900 border-zinc-800">
          <div 
            ref={containerRef}
            id="VkIdSdkOneTap"
            className="flex justify-center items-center min-h-[44px] rounded-xl overflow-hidden"
          />
          
          {isProcessingCallback ? (
            <div className="py-6 text-center">
              <div className="inline-block w-6 h-6 rounded-full border-2 border-zinc-700 border-t-[#0077FF] animate-spin"></div>
              <p className="mt-3 text-sm text-zinc-500">Обработка авторизации...</p>
            </div>
          ) : !isVKIDReady && (
            <div className="py-6 text-center">
              <div className="inline-block w-6 h-6 rounded-full border-2 border-zinc-700 border-t-[#0077FF] animate-spin"></div>
              <p className="mt-3 text-sm text-zinc-500">
                {isVKIDLoaded ? 'Инициализация...' : 'Загрузка...'}
              </p>
            </div>
          )}
        </div>

        {/* No App ID warning */}
        {!appId && (
          <div className="p-4 mt-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
            <p className="text-sm font-medium text-amber-400">Ошибка конфигурации</p>
            <p className="mt-1 text-xs text-amber-400/70">Не указан VK App ID</p>
          </div>
        )}

        {skin && <SkinLegal skin={skin} className="mt-6" />}
      </div>
    </div>
  );
};

export default VKAuth; 